<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { onKeyStroke } from '@vueuse/core'
import { Plus, ShieldCheck } from 'lucide-vue-next'
import { allNavItems } from '@/lib/nav-items'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { useConfigStore } from '@/stores/config'
import { useSessionListStore } from '@/modules/session-dashboard/stores/useSessionListStore'
import { useIsAdmin } from '@/modules/admin/composables/useIsAdmin'
const route = useRoute()
const configStore = useConfigStore()
const sessionStore = useSessionListStore()
const { isAdmin } = useIsAdmin()

const navItems = computed(() =>
  allNavItems.filter((item) => configStore.isFeatureEnabled(item.featureKey)),
)

onKeyStroke('n', (e) => {
  if (e.altKey) {
    e.preventDefault()
    sessionStore.isCreateDialogOpen = true
  }
})

const year = new Date().getFullYear()

function resolveHref(href: string): string {
  if (!href.startsWith('http') && !href.startsWith('mailto:') && href.includes('@')) {
    return `mailto:${href}`
  }
  return href
}

const externalLinks = computed(() => {
  const l = configStore.links
  return [
    { label: 'Website', href: l.website },
    { label: 'Privacy Policy', href: l.privacyPolicy },
    { label: 'Terms of Service', href: l.termsOfService },
    { label: 'Support', href: l.support },
  ]
    .filter((link): link is { label: string; href: string } => link.href !== null)
    .map((link) => ({ ...link, href: resolveHref(link.href) }))
})

function isActive(to: string) {
  if (to === '/') return route.path === '/'
  return route.path.startsWith(to)
}
</script>

<template>
  <Sidebar collapsible="none" side="left" class="h-full">
    <SidebarHeader class="px-4 py-3">
      <Button
        class="w-full shadow-md shadow-primary/40"
        @click="sessionStore.isCreateDialogOpen = true"
      >
        <Plus class="size-4" />
        New Session
        <kbd class="ml-auto flex items-center gap-0.5 font-mono text-[9px] opacity-50">
          <span class="rounded border border-primary-foreground/40 px-1 py-0.5 leading-none"
            >Alt</span
          >
          <span class="rounded border border-primary-foreground/40 px-1 py-0.5 leading-none"
            >N</span
          >
        </kbd>
      </Button>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup aria-label="Main navigation">
        <SidebarGroupLabel>Menu</SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem v-for="item in navItems" :key="item.to">
            <SidebarMenuButton as-child :is-active="isActive(item.to)">
              <RouterLink :to="item.to" class="flex items-center gap-2">
                <component :is="item.icon" class="size-4" />
                <span>{{ item.label }}</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
      <SidebarGroup v-if="isAdmin" aria-label="Admin">
        <SidebarGroupLabel>Admin</SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton as-child :is-active="isActive('/admin')">
              <RouterLink to="/admin/questions" class="flex items-center gap-2">
                <ShieldCheck class="size-4" />
                <span>Questions</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter class="px-4 py-3 space-y-2">
      <p class="text-xs text-muted-foreground">
        &copy; {{ year }} {{ configStore.branding.copyrightHolder }}
      </p>

      <div v-if="externalLinks.length" class="flex flex-wrap gap-x-3 gap-y-1">
        <a
          v-for="link in externalLinks"
          :key="link.label"
          :href="link.href"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {{ link.label }}
        </a>
      </div>
    </SidebarFooter>
  </Sidebar>

</template>
